import { combineEpics, ofType } from "redux-observable"
import { flatMap, catchError } from "rxjs/operators"
import { of, from, EMPTY } from "rxjs"
import { Epic } from "../../epics"

import * as actions from "./actions"
import env from "../../env"
import { RequestCancelledError } from "../../errors"

export const defaultDatabaseVersionError =
    "An error occurred when trying to verify the application version."
export const defaultLoginError = "An error occurred while logging in."
export const defaultLogoUrl = "resource/logos/logo.png"
export const defaultLogoutError = "An error occurred while logging out."
export const defaultMaintenanceError =
    "The application is not available for the moment. Maintenance is ongoing."
export const defaultResetPasswordError =
    "An error occurred while resetting password."

export const requestLogoEpic: Epic = (
    action$,
    _state$,
    { auth: { authAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_LOGO),
        flatMap(() =>
            from(authAPI.getLogo()).pipe(
                flatMap((url: string) => of(actions.receiveLogo(url))),
                catchError(() => of(actions.receiveLogo(defaultLogoUrl))),
            ),
        ),
    )

export const requestDatabaseVersionEpic: Epic = (
    action$,
    _state$,
    { auth: { authAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_DATABASE_VERSION),
        flatMap(() =>
            from(authAPI.checkDatabaseVersion()).pipe(
                flatMap(() => of(actions.receiveDatabaseVersionSuccess())),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    return of(
                        actions.receiveDatabaseVersionError(
                            error.statusText || defaultDatabaseVersionError,
                        ),
                    )
                }),
            ),
        ),
    )

export const requestMaintenanceEpic: Epic = (
    action$,
    _state$,
    { auth: { authAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_MAINTENANCE),
        flatMap(() =>
            from(authAPI.checkMaintenance()).pipe(
                flatMap(() => of(actions.receiveMaintenanceSuccess())),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    return of(
                        actions.receiveMaintenanceError(
                            error.statusText || defaultMaintenanceError,
                        ),
                    )
                }),
            ),
        ),
    )

export const requestLoginEpic: Epic = (
    action$,
    state$,
    { auth: { authAPI, windowLocation } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_LOGIN),
        flatMap(() => {
            const {
                auth: {
                    loginPage: { username, password, remember },
                },
            } = state$.value

            return from(authAPI.login(username, password, remember)).pipe(
                flatMap(() => {
                    windowLocation.replace(
                        env.applicationPath +
                            "/login" +
                            windowLocation.search +
                            encodeURIComponent(
                                encodeURIComponent(windowLocation.hash),
                            ),
                    )
                    return EMPTY
                }),
                catchError((error: Response | Error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    if ("json" in error && error.json) {
                        return from(error.json()).pipe(
                            flatMap(({ ErrorMessage }) =>
                                of(
                                    actions.receiveLoginError(
                                        ErrorMessage || defaultLoginError,
                                    ),
                                ),
                            ),
                        )
                    }
                    return of(actions.receiveLoginError(defaultLoginError))
                }),
            )
        }),
    )

export const requestLogoutEpic: Epic = (
    action$,
    _state$,
    { auth: { authAPI, windowLocation } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_LOGOUT),
        flatMap(() =>
            from(authAPI.logout()).pipe(
                flatMap(() => {
                    setTimeout(function () {
                        windowLocation.replace(env.applicationPath + "/")
                    }, 5000)
                    return EMPTY
                }),
                catchError((error: Response | Error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    if ("json" in error && error.json) {
                        return from(error.json()).pipe(
                            flatMap(({ ErrorMessage }) =>
                                of(
                                    actions.receiveLogoutError(
                                        ErrorMessage || defaultLogoutError,
                                    ),
                                ),
                            ),
                        )
                    }
                    return of(actions.receiveLogoutError(defaultLogoutError))
                }),
            ),
        ),
    )

export const requestRestorePasswordEpic: Epic = (
    action$,
    state$,
    { auth: { authAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_RESTORE_PASSWORD),
        flatMap(() => {
            const {
                auth: {
                    recoverPasswordPage: { email },
                },
            } = state$.value

            return from(authAPI.restorePassword(email)).pipe(
                flatMap(() => of(actions.receiveRestorePasswordSuccess())),
                catchError((error: Response | Error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    if ("json" in error && error.json) {
                        return from(error.json()).pipe(
                            flatMap(({ ErrorMessage }) =>
                                of(
                                    actions.receiveRestorePasswordError(
                                        ErrorMessage ||
                                            defaultResetPasswordError,
                                    ),
                                ),
                            ),
                        )
                    }
                    return of(
                        actions.receiveRestorePasswordError(
                            defaultResetPasswordError,
                        ),
                    )
                }),
            )
        }),
    )

export default combineEpics(
    requestLogoEpic,
    requestDatabaseVersionEpic,
    requestMaintenanceEpic,
    requestLoginEpic,
    requestLogoutEpic,
    requestRestorePasswordEpic,
)
